import React from "react";
import { Link } from "react-router-dom";
import { Info, Receipt, Users, Buildings, Warning, ShieldCheck, ChatCircle } from "@phosphor-icons/react";

const Card = ({ icon: Icon, title, children, testid }) => (
  <section className="card-list mt-4 p-6" data-testid={testid}>
    <div className="flex items-center gap-2.5">
      <span className="grid h-10 w-10 place-items-center rounded-xl border border-ppc bg-ppc-bg">
        <Icon size={18} color="#9184d9" />
      </span>
      <h2 className="text-base text-ppc-text md:text-lg">{title}</h2>
    </div>
    <div className="mt-4 space-y-3 text-[13.5px] leading-relaxed text-ppc-muted">{children}</div>
  </section>
);

export default function FAQItaly() {
  return (
    <div className="mx-auto max-w-3xl px-5 pb-40 pt-10 md:pb-16 lg:px-8" data-testid="faq-italy-page">
      <div className="card-hero p-6">
        <span className="glass-pill inline-flex items-center gap-1.5 text-[12px] text-ppc-text">🇮🇹 Italia</span>
        <h1 className="mt-4 text-3xl leading-tight tracking-[-0.02em] text-ppc-text md:text-4xl">
          Domande frequenti — lavoro domestico in Italia
        </h1>
        <p className="mt-3 text-sm text-ppc-muted">
          Come funziona il lavoro domestico occasionale e continuativo in Italia: Libretto Famiglia INPS, contratto CCNL
          per colf, badanti e baby-sitter, Partita IVA e prestazioni occasionali. Informazioni generali per famiglie e
          fornitori su PlinPlinCare.
        </p>
      </div>

      <Card icon={Users} title="Lavoro saltuario per una famiglia: il Libretto Famiglia INPS" testid="faq-libretto">
        <p>
          Se sei una <strong className="text-ppc-text">persona fisica</strong> (non impresa e non professionista) e ti
          serve aiuto <strong className="text-ppc-text">occasionale e saltuario</strong> — piccoli lavori domestici,
          pulizie, giardinaggio, assistenza domiciliare, baby-sitting o ripetizioni — puoi pagare la prestazione con il{" "}
          <strong className="text-ppc-text">Libretto Famiglia</strong> dell'INPS.
        </p>
        <ul className="list-disc space-y-1.5 pl-5">
          <li>Ogni titolo di pagamento vale <strong className="text-ppc-text">10 €</strong> e remunera fino a un'ora di lavoro: circa 8 € netti al prestatore, il resto è contribuzione INPS Gestione Separata, premio INAIL e oneri di gestione.</li>
          <li>Limiti per anno civile: <strong className="text-ppc-text">5.000 €</strong> per ciascun prestatore (con tutti gli utilizzatori), <strong className="text-ppc-text">10.000 €</strong> per ciascun utilizzatore (con tutti i prestatori) e <strong className="text-ppc-text">2.500 €</strong> per le prestazioni tra lo stesso prestatore e lo stesso utilizzatore.</li>
          <li>La famiglia comunica la prestazione <strong className="text-ppc-text">entro il 3 del mese successivo</strong> sul Portale delle prestazioni occasionali INPS (nuovo portale operativo da gennaio 2026, accesso con SPID, CIE, CNS o eIDAS). L'INPS paga il prestatore entro il 15 del mese successivo.</li>
          <li>Per pensionati, studenti under 25, disoccupati e percettori di sostegno al reddito i compensi si computano al 75% ai fini del limite dell'utilizzatore.</li>
          <li>Non si può usare il Libretto Famiglia con una persona che ha, o ha avuto negli ultimi 6 mesi, un rapporto di lavoro subordinato o di collaborazione con la stessa famiglia.</li>
        </ul>
      </Card>

      <Card icon={Receipt} title="Lavoro continuativo o con orario fisso: contratto CCNL lavoro domestico" testid="faq-ccnl">
        <p>
          Se l'aiuto è <strong className="text-ppc-text">regolare, continuativo o con orario fisso</strong> (colf,
          badante, baby-sitter), serve un <strong className="text-ppc-text">regolare contratto di lavoro domestico</strong>{" "}
          secondo il CCNL di settore: la famiglia è il datore di lavoro.
        </p>
        <ul className="list-disc space-y-1.5 pl-5">
          <li>Comunicazione di assunzione all'INPS <strong className="text-ppc-text">entro 24 ore</strong> dall'inizio del rapporto (Cassetto previdenziale per i lavoratori domestici).</li>
          <li>Contributi <strong className="text-ppc-text">trimestrali</strong>: 10 aprile, 10 luglio, 10 ottobre, 10 gennaio. L'aliquota complessiva è circa il 33% della retribuzione (circa 26% a carico del datore, 7% del lavoratore), oltre alla Cassa Colf dove prevista.</li>
          <li>Dal 2026 il pagamento è <strong className="text-ppc-text">solo digitale</strong>: avvisi PagoPA generati dal Portale dei Pagamenti INPS, App INPS o App IO (nessun bollettino cartaceo, salvo deroga per datori over 76).</li>
          <li>Le tabelle retributive e i contributi orari 2026 sono aggiornati dalla Circolare INPS n. 9 del 3 febbraio 2026 (indice ISTAT +1,4%).</li>
          <li>Benefici fiscali: contributi deducibili fino a <strong className="text-ppc-text">1.549,37 €</strong> l'anno; per l'assistenza a persone non autosufficienti detrazione IRPEF del 19% su una spesa massima di 2.100 € se il reddito non supera 40.000 €.</li>
          <li><strong className="text-ppc-text">Colf e badanti non possono lavorare con Partita IVA</strong>: sono lavoratori subordinati, non autonomi.</li>
        </ul>
      </Card>

      <Card icon={Buildings} title="Imprese, professionisti e Partita IVA" testid="faq-piva">
        <p>
          Imprese di pulizie, giardinieri, tuttofare, cuochi e insegnanti che lavorano come{" "}
          <strong className="text-ppc-text">autonomi o società</strong> operano con Partita IVA ed emettono regolare
          fattura per il servizio.
        </p>
        <ul className="list-disc space-y-1.5 pl-5">
          <li>Per attività realmente occasionali è possibile la <strong className="text-ppc-text">prestazione occasionale con ritenuta d'acconto</strong>, entro 5.000 € l'anno di compensi.</li>
          <li>Il fornitore è responsabile dei propri obblighi fiscali e contributivi e della fatturazione al cliente.</li>
          <li>Su PlinPlinCare il prezzo indicato è quello del fornitore: il fornitore riceve il 100% del prezzo del servizio, mentre la commissione di prenotazione del 10% e le spese di pagamento sono a carico del cliente.</li>
        </ul>
      </Card>

      <Card icon={Warning} title="Lavoro non dichiarato: cosa si rischia" testid="faq-nero">
        <p>
          Il lavoro nero e il mancato versamento dei contributi espongono la famiglia a{" "}
          <strong className="text-ppc-text">sanzioni amministrative da 250 a 2.500 €</strong>, al recupero dei contributi
          con interessi e, nei casi più gravi, a conseguenze penali. Regolarizzare con Libretto Famiglia o con un
          contratto CCNL protegge sia la famiglia sia il lavoratore (infortuni, malattia, pensione).
        </p>
      </Card>

      <Card icon={ShieldCheck} title="Il ruolo di PlinPlinCare" testid="faq-role">
        <p>
          PlinPlinCare è un <strong className="text-ppc-text">marketplace</strong> che mette in contatto famiglie e
          fornitori indipendenti: non è il datore di lavoro e non è un'agenzia di somministrazione. La scelta della forma
          corretta (Libretto Famiglia, contratto CCNL, fattura con Partita IVA) spetta alla famiglia e al fornitore.
        </p>
        <p>
          I fornitori possono <Link to="/verification" className="text-primary-green" data-testid="faq-verify-link">verificare il proprio profilo</Link> con
          foto, selfie dal vivo e dati legali: i dati personali non sono mai mostrati nell'app e nelle liste dei servizi
          compare solo il nome, senza cognome.
        </p>
      </Card>

      <div className="card-focal mt-4 p-6" data-testid="faq-disclaimer">
        <div className="flex items-center gap-2.5">
          <Info size={18} color="#9184d9" />
          <h2 className="text-base text-ppc-text md:text-lg">Avvertenza</h2>
        </div>
        <p className="mt-3 text-[13px] leading-relaxed text-ppc-muted">
          Queste informazioni sono di carattere generale e aggiornate alle regole INPS 2026; non costituiscono
          consulenza fiscale o legale. Per il tuo caso specifico rivolgiti a un consulente del lavoro, a un CAF o a
          un'associazione di categoria.
        </p>
        <p className="mt-3 text-[13px] leading-relaxed text-ppc-muted">
          Per domande su <strong className="text-ppc-text">PlinPlinCare</strong> (prenotazioni, pagamenti, commissioni,
          verifica del profilo) chiedi direttamente al nostro assistente: risponde in italiano, subito.
        </p>
        <button
          type="button"
          data-testid="faq-open-chat-btn"
          onClick={() => window.dispatchEvent(new Event("ppc:open-chat"))}
          className="btn-primary mt-4 inline-flex h-11 items-center gap-2 rounded-xl px-5 text-sm"
        >
          <ChatCircle size={17} /> Chiedi all'assistente PlinPlinCare
        </button>
        <p className="mt-4 text-[13px] text-ppc-muted">
          Preferisci scrivere? <a href="mailto:info@jamesbassholding.com" className="text-primary-green">info@jamesbassholding.com</a>
        </p>
        <p className="mt-3 text-[13px] text-ppc-muted">
          Vedi anche le <Link to="/legal" className="text-primary-green">condizioni d'uso e la privacy policy</Link>.
        </p>
      </div>
    </div>
  );
}
